import { lazy, Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { AppShell } from '@/components/layout/AppShell';
import { LoadingState } from '@/components/feedback/LoadingState';
import { RequireAuth } from '@/auth/RequireAuth';
import { RequireAdmin } from '@/auth/RequireAdmin';

const LoginPage       = lazy(() => import('@/pages/LoginPage').then((m) => ({ default: m.LoginPage })));
const ForgotPasswordPage = lazy(() => import('@/pages/ForgotPasswordPage').then((m) => ({ default: m.ForgotPasswordPage })));
const ResetPasswordPage = lazy(() => import('@/pages/ResetPasswordPage').then((m) => ({ default: m.ResetPasswordPage })));
const AttributionPage = lazy(() => import('@/pages/AttributionPage').then((m) => ({ default: m.AttributionPage })));
const SummaryView     = lazy(() => import('@/features/attribution/SummaryView').then((m) => ({ default: m.SummaryView })));
const PnLView         = lazy(() => import('@/features/attribution/PnLView').then((m) => ({ default: m.PnLView })));
const ExposureView    = lazy(() => import('@/features/attribution/ExposureView').then((m) => ({ default: m.ExposureView })));
const TreasuryView    = lazy(() => import('@/features/attribution/TreasuryView').then((m) => ({ default: m.TreasuryView })));
const CommoditiesView   = lazy(() => import('@/features/attribution/CommoditiesView').then((m) => ({ default: m.CommoditiesView })));
const IntelligenceView  = lazy(() => import('@/features/intelligence/IntelligenceView').then((m) => ({ default: m.IntelligenceView })));
const CustomTabView     = lazy(() => import('@/features/attribution/CustomTabView').then((m) => ({ default: m.CustomTabView })));
const DashboardsPage  = lazy(() => import('@/pages/DashboardsPage').then((m) => ({ default: m.DashboardsPage })));
const SettingsPage    = lazy(() => import('@/pages/SettingsPage').then((m) => ({ default: m.SettingsPage })));
const AdminUsersPage  = lazy(() => import('@/pages/AdminUsersPage').then((m) => ({ default: m.AdminUsersPage })));
const PrintPreviewPage = lazy(() => import('@/pages/PrintPreviewPage').then((m) => ({ default: m.PrintPreviewPage })));
const PrintExportPage = lazy(() => import('@/pages/PrintExportPage').then((m) => ({ default: m.PrintExportPage })));

export function AppRouter() {
  return (
    <Suspense fallback={<LoadingState variant="spinner" />}>
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />

        <Route
          path="/"
          element={
            <RequireAuth>
              <AppShell>
                <Navigate to="/attribution/summary" replace />
              </AppShell>
            </RequireAuth>
          }
        />

        {/* Attribution Reports — shell with nested sub-routes */}
        <Route
          path="/attribution"
          element={
            <RequireAuth>
              <AppShell>
                <AttributionPage />
              </AppShell>
            </RequireAuth>
          }
        >
          <Route index element={<Navigate to="summary" replace />} />
          <Route path="summary"     element={<SummaryView />} />
          <Route path="pnl"         element={<PnLView />} />
          <Route path="exposure"    element={<ExposureView />} />
          <Route path="treasury"    element={<TreasuryView />} />
          <Route path="commodities"      element={<CommoditiesView />} />
          <Route path="intelligence"     element={<IntelligenceView />} />
          <Route path="custom/:tabId"    element={<CustomTabView />} />
        </Route>

        {/* Dashboards */}
        <Route
          path="/dashboards/*"
          element={
            <RequireAuth>
              <AppShell>
                <DashboardsPage />
              </AppShell>
            </RequireAuth>
          }
        />

        {/* Print preview */}
        <Route
          path="/print-preview"
          element={
            <RequireAuth>
              <PrintPreviewPage />
            </RequireAuth>
          }
        />

        <Route
          path="/print-export"
          element={
            <RequireAuth>
              <PrintExportPage />
            </RequireAuth>
          }
        />

        {/* Settings */}
        <Route
          path="/settings"
          element={
            <RequireAuth>
              <AppShell>
                <SettingsPage />
              </AppShell>
            </RequireAuth>
          }
        />

        {/* Admin — user management */}
        <Route
          path="/admin/users"
          element={
            <RequireAuth>
              <RequireAdmin>
                <AppShell>
                  <AdminUsersPage />
                </AppShell>
              </RequireAdmin>
            </RequireAuth>
          }
        />

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  );
}
